import { Component } from '@theme/component';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STORAGE_KEY_RECOMMENDATIONS = 'recommendations';
const STORAGE_KEY_SURVEY_STARTED_EMAIL = 'thuysvers_ecomm_survey_started_email';
const STORAGE_KEY_SURVEY_COMPLETED_EMAIL = 'thuysvers_ecomm_survey_completed_email';
const EVENT_RECOMMENDATIONS_UPDATED = 'ecomm-recommendations-updated';

/**
 * A custom element that manages the Thuysvers Survey Forms.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} closeSurveyButton - The close survey button element
 * @property {HTMLButtonElement} startSurveyButton - The start survey button element
 * @property {HTMLButtonElement} prevSurveyButton - The previous survey button element
 * @property {HTMLButtonElement} nextSurveyButton - The next survey button element
 * @property {HTMLElement[]} surveyStep[] - The survey step elements
 * @property {HTMLElement} surveyFooterContentWrapper - The survey footer content wrapper element
 * @property {HTMLButtonElement[]} qtyBtnInc[] - Quantity increment buttons
 * @property {HTMLButtonElement[]} qtyBtnDec[] - Quantity decrement buttons
 * @property {HTMLInputElement[]} qtyInput[] - Quantity inputs
 * @property {HTMLElement} surveySubmitWrapper - The survey submit wrapper element
 * @extends {Component<Refs>}
 */
export class ThuysversSurveyFormComponent extends Component {
  /** @type {HTMLFormElement | null} */
  formEl = null;

  /** @type {boolean} */
  isSubmitting = false;

  /** @type {number} */
  profileCountSelected = 1;

  /** @type {Map<HTMLElement, (event: Event) => void>} */
  qtyBtnHandlers = new Map();

  /** @type {Map<HTMLInputElement, (event: Event) => void>} */
  qtyInputHandlers = new Map();

  connectedCallback() {
    try {
      super.connectedCallback();
      this.refs.closeSurveyButton?.addEventListener('click', this.#closeSurveyForm);
      this.refs.startSurveyButton?.addEventListener('click', this.#startSurvey);
      this.refs.prevSurveyButton?.addEventListener('click', this.#prevSurveyStep);
      this.refs.nextSurveyButton?.addEventListener('click', this.#nextSurveyStep);

      this.formEl = this.querySelector('form[data-thuysvers-survey]');
      this.formEl?.addEventListener('input', this.#onFormInputOrChange, true);
      this.formEl?.addEventListener('change', this.#onFormInputOrChange, true);
      this.formEl?.addEventListener('keydown', this.#onFormKeyDown, true);

      this.bindQtyControlsStatic();

      // Initial state (important if the active step is already an email step)
      this.updateNavState();
    } catch (error) {
      console.error('[THUYSVERS-SURVEY-FORMS] Error in connectedCallback', error);
      throw error;
    }
  }

  disconnectedCallback() {
    // Remove listeners we added in connectedCallback
    this.refs.closeSurveyButton?.removeEventListener('click', this.#closeSurveyForm);
    this.refs.startSurveyButton?.removeEventListener('click', this.#startSurvey);
    this.refs.prevSurveyButton?.removeEventListener('click', this.#prevSurveyStep);
    this.refs.nextSurveyButton?.removeEventListener('click', this.#nextSurveyStep);

    this.formEl?.removeEventListener('input', this.#onFormInputOrChange, true);
    this.formEl?.removeEventListener('change', this.#onFormInputOrChange, true);
    this.formEl?.removeEventListener('keydown', this.#onFormKeyDown, true);
    this.formEl = null;

    // Remove qty listeners we bound in bindQtyControlsStatic
    for (const [btn, handler] of this.qtyBtnHandlers.entries()) {
      btn.removeEventListener('click', handler);
    }
    this.qtyBtnHandlers.clear();

    for (const [input, handler] of this.qtyInputHandlers.entries()) {
      input.removeEventListener('input', handler);
      input.removeEventListener('change', handler);
    }
    this.qtyInputHandlers.clear();

    super.disconnectedCallback();
  }

  /**
   * @returns {HTMLElement | undefined}
   */
  getActiveStep() {
    return this.refs.surveyStep?.find((step) => step.classList.contains('active'));
  }

  /**
   * @returns {number}
   */
  getActiveStepIndex() {
    return this.refs.surveyStep?.findIndex((step) => step.classList.contains('active')) ?? -1;
  }

  /**
   * @param {number} index
   */
  setActiveStepIndex(index) {
    if (!Array.isArray(this.refs.surveyStep)) return;
    if (index < 0 || index >= this.refs.surveyStep.length) return;

    const currentIndex = this.getActiveStepIndex();
    if (currentIndex === index) return;

    this.refs.surveyStep[currentIndex]?.classList.remove('active');
    this.refs.surveyStep[index]?.classList.add('active');
  }

  updateNavState() {
    const activeStep = this.getActiveStep();
    if (!activeStep) return;

    // Toggle footer (we keep it hidden on the "start" step)
    const activeIndex = this.getActiveStepIndex();
    if (activeStep.dataset?.fieldType === 'Submit') {
      this.refs.surveyFooterContentWrapper?.classList.add('no-disp');
      this.refs.prevSurveyButton?.classList.add('visibility-hidden');
      this.refs.closeSurveyButton?.classList.add('no-disp');
    } else if (activeIndex <= 0) {
      this.refs.surveyFooterContentWrapper?.classList.add('no-disp');
      this.refs.closeSurveyButton?.classList.remove('no-disp');
    } else {
      this.refs.surveyFooterContentWrapper?.classList.remove('no-disp');
      this.refs.prevSurveyButton?.classList.remove('visibility-hidden');
      this.refs.closeSurveyButton?.classList.remove('no-disp');
    }

    // Update next button label to match current step
    const nextLabel = activeStep.dataset?.nextLabel;
    if (this.refs.nextSurveyButton && nextLabel) {
      this.refs.nextSurveyButton.textContent = nextLabel;
    }

    // Enable/disable next based on current step rules
    if (this.refs.nextSurveyButton) {
      this.refs.nextSurveyButton.disabled = !this.canAdvanceFromActiveStep();
    }
  }

  /**
   * Syncs email validity against the regex by setting `setCustomValidity`.
   * Returns the result of `checkValidity()`.
   * @param {HTMLInputElement} emailInput
   * @returns {boolean}
   */
  syncEmailValidity(emailInput) {
    const value = emailInput.value.trim();

    // Let "required" handle empty values (but keep Next disabled).
    if (!value) {
      emailInput.setCustomValidity('');
      return emailInput.checkValidity();
    }

    if (!EMAIL_REGEX.test(value)) {
      emailInput.setCustomValidity('Please enter a valid email address.');
      return false;
    }

    emailInput.setCustomValidity('');
    return emailInput.checkValidity();
  }

  /**
   * @returns {boolean}
   */
  canAdvanceFromActiveStep() {
    const activeStep = this.getActiveStep();
    if (!activeStep) return false;

    const fieldType = activeStep.dataset?.fieldType;
    if (fieldType === 'Email') {
      const emailInput = activeStep.querySelector('input[type="email"]');
      if (!(emailInput instanceof HTMLInputElement)) return false;
      return this.syncEmailValidity(emailInput);
    }

    // Single choice (radio): must choose one option
    /** @type {HTMLInputElement[]} */
    const radioInputs = Array.from(activeStep.querySelectorAll('input[type="radio"]'));
    if (radioInputs.length) {
      const anyChecked = radioInputs.some((el) => el.checked);
      // Attach validity to the first radio so reportValidity() shows a message.
      const firstRadio = radioInputs[0];
      if (firstRadio) firstRadio.setCustomValidity(anyChecked ? '' : 'Please select an option.');
      return anyChecked;
    }

    // Multiple choice (checkbox): must choose at least N options (default 1, can be configured via data-min-checked)
    /** @type {HTMLInputElement[]} */
    const checkboxInputs = Array.from(activeStep.querySelectorAll('input[type="checkbox"]'));
    if (checkboxInputs.length) {
      const checkedCount = checkboxInputs.reduce((acc, el) => acc + (el.checked ? 1 : 0), 0);
      const requiredMin = 4;

      const firstCheckbox = checkboxInputs[0];
      if (firstCheckbox) firstCheckbox.setCustomValidity(checkedCount >= requiredMin ? '' : `Selecteer minimaal ${requiredMin} opties.`);
      return checkedCount >= requiredMin;
    }

    // Fallback: if step contains any required field, respect native validity.
    const requiredField = activeStep.querySelector('input[required], select[required], textarea[required]');
    if (requiredField instanceof HTMLInputElement || requiredField instanceof HTMLSelectElement || requiredField instanceof HTMLTextAreaElement) {
      return requiredField.checkValidity();
    }

    return true;
  }

  /**
   * @param {Event} event
   */
  #onFormInputOrChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    // Only re-evaluate button state when changing inputs in the active step.
    const activeStep = this.getActiveStep();
    if (!activeStep) return;
    if (!activeStep.contains(target)) return;

    // Quantity is handled by dedicated listeners. Avoid any nav-state logic here.
    if (target instanceof HTMLInputElement && target.type === 'number' && target.classList.contains('thuysvers-survey-form-qty-input')) return;

    this.updateNavState();
  }

  /**
   * Returns the gadget progress endpoint if configured.
   * Prefer `data-gadget-progress-endpoint`; otherwise try to infer from `data-gadget-endpoint`.
   * @returns {string}
   */
  getGadgetProgressEndpoint() {
    const form = this.formEl;
    if (!form) return '';

    const explicit = String(form.getAttribute('data-gadget-progress-endpoint') || '').trim();
    if (explicit) return explicit;

    const submitEndpoint = String(form.getAttribute('data-gadget-endpoint') || '').trim();
    if (!submitEndpoint) return '';

    return '';
  }

  /**
   * Extracts email from the form (same logic as buildSubmissionJson).
   * @returns {string}
   */
  getSubmitterEmailFromForm() {
    const form = this.formEl;
    if (!form) return '';
    try {
      const fd = new FormData(form);
      const emailInput = form.querySelector('input[type="email"]');
      return (
        String(fd.get('email') || '').trim() ||
        (emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '')
      );
    } catch {
      return '';
    }
  }

  /**
   * Sends a "started/completed" event to Gadget so it can upsert a Klaviyo profile property.
   * @param {'started'|'completed'} status
   * @param {string} email
   * @param {Record<string, any>=} extra
   */
  async sendSurveyProgress(status, email, extra) {
    const endpoint = this.getGadgetProgressEndpoint();
    if (!endpoint) return;

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          status,
          email,
          occurredAt: new Date().toISOString(),
          ...(extra || {}),
        }),
      });

      if (!res.ok) {
        console.warn('[THUYSVERS-SURVEY] Survey progress request failed', { status: res.status });
      }
    } catch (error) {
      console.warn('[THUYSVERS-SURVEY] Survey progress request errored', error);
    }
  }

  /**
   * Marks `thuysvers_survey_form = started` once per email in this browser session.
   */
  async maybeTrackSurveyStarted() {
    const email = this.getSubmitterEmailFromForm();
    if (!email || !EMAIL_REGEX.test(email)) return;

    try {
      const prev = sessionStorage.getItem(STORAGE_KEY_SURVEY_STARTED_EMAIL) || '';
      const completed = sessionStorage.getItem(STORAGE_KEY_SURVEY_COMPLETED_EMAIL) || '';
      // If this email already completed in this session, no need to send "started".
      if (completed && completed === email) return;
      if (prev && prev === email) return;

      sessionStorage.setItem(STORAGE_KEY_SURVEY_STARTED_EMAIL, email);
    } catch {
      // Ignore storage failures; still attempt to send.
    }

    await this.sendSurveyProgress('started', email);
  }

  /**
   * Bind +/- clicks for quantity selectors using static array refs:
   * - `ref="qtyBtnInc[]"`, `ref="qtyBtnDec[]"`, `ref="qtyInput[]"`
   */
  bindQtyControlsStatic() {
    /** @type {HTMLButtonElement[]} */
    const incButtons = Array.isArray(this.refs.qtyBtnInc) ? this.refs.qtyBtnInc : [];
    /** @type {HTMLButtonElement[]} */
    const decButtons = Array.isArray(this.refs.qtyBtnDec) ? this.refs.qtyBtnDec : [];
    /** @type {HTMLInputElement[]} */
    const inputs = Array.isArray(this.refs.qtyInput) ? this.refs.qtyInput : [];

    /**
     * @param {HTMLButtonElement} btn
     * @param {number} delta
     */
    const bindButton = (btn, delta) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      if (this.qtyBtnHandlers.has(btn)) return;

      /** @param {Event} event */
      const handler = (event) => {
        event.preventDefault();

        const qtyWrapper = btn.closest('.thuysvers-survey-form-qty');
        if (!(qtyWrapper instanceof HTMLElement)) return;

        const inputEl = qtyWrapper.querySelector('input.thuysvers-survey-form-qty-input');
        if (!(inputEl instanceof HTMLInputElement)) return;

        this.bumpQtyInputValue(inputEl, delta);
        // this.syncSoupTypeVisibilityFromQtyInput(inputEl);
      };

      this.qtyBtnHandlers.set(btn, handler);
      btn.addEventListener('click', handler);
    };

    for (const btn of incButtons) bindButton(btn, 1);
    for (const btn of decButtons) bindButton(btn, -1);

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (this.qtyInputHandlers.has(input)) continue;

      /** @param {Event} _event */
      const onValueChange = (_event) => {
        this.normalizeQtyInputValue(input);
        // this.syncSoupTypeVisibilityFromQtyInput(input);
      };

      this.qtyInputHandlers.set(input, onValueChange);
      input.addEventListener('input', onValueChange);
      input.addEventListener('change', onValueChange);
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {number}
   */
  getQtyInputNumber(input) {
    const raw = input.value;
    const num = Number.parseInt(String(raw ?? '').trim(), 10);
    return Number.isFinite(num) ? num : 0;
  }

  /**
   * @param {HTMLInputElement} input
   */
  normalizeQtyInputValue(input) {
    const min = Number.isFinite(Number(input.min)) ? Number(input.min) : 0;
    const value = this.getQtyInputNumber(input);
    input.value = String(Math.max(min, value));
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} delta
   */
  bumpQtyInputValue(input, delta) {
    const min = Number.isFinite(Number(input.min)) ? Number(input.min) : 0;
    const step = Number.isFinite(Number(input.step)) && Number(input.step) > 0 ? Number(input.step) : 1;
    const current = this.getQtyInputNumber(input);
    const next = Math.max(min, current + delta * step);

    input.value = String(next);
    // Fire an input event so any listeners (and native UI) stay consistent.
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // /**
  //  * Show/hide soup type selector when soup qty > 0.
  //  * @param {HTMLInputElement} qtyInput
  //  */
  // syncSoupTypeVisibilityFromQtyInput(qtyInput) {
  //   const extrasWrapper = qtyInput.closest('.thuysvers-survey-form-extras-wrapper');
  //   if (!(extrasWrapper instanceof HTMLElement)) return;

  //   const soupTypeWrapper = extrasWrapper.querySelector('.thuysvers-survey-form-soup-type-wrapper');
  //   if (!(soupTypeWrapper instanceof HTMLElement)) return;

  //   const qty = this.getQtyInputNumber(qtyInput);
  //   soupTypeWrapper.classList.toggle('no-disp', qty <= 0);
  // }

  // hideSoupTypeOnFormReset() {
  //   const soupTypeWrapper = this.formEl?.querySelector('.thuysvers-survey-form-soup-type-wrapper');
  //   if (!(soupTypeWrapper instanceof HTMLElement)) return;
  //   soupTypeWrapper.classList.add('no-disp');
  // }

  /**
   * Prevent "Enter" from submitting the form; instead try to advance to next step.
   * @param {KeyboardEvent} event
   */
  #onFormKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    // Allow default behavior inside textarea (not currently used, but safer).
    const target = event.target;
    if (target instanceof HTMLTextAreaElement) return;

    event.preventDefault();

    // Mirror the "Next" button behavior
    if (this.canAdvanceFromActiveStep()) {
      this.#nextSurveyStep();
      return;
    }

    // If blocked, show native validation UI (especially for email).
    const activeStep = this.getActiveStep();
    const emailInput = activeStep?.querySelector('input[type="email"]');
    if (emailInput instanceof HTMLInputElement) emailInput.reportValidity();
  }

  #startSurvey = () => {
    this.setActiveStepIndex(1);
    this.updateNavState();
  }

  #prevSurveyStep = () => {
    const currentStep = this.getActiveStep();
    const currentIndex = this.getActiveStepIndex();
    if (!currentStep || currentIndex <= 0) return;

    // Special case: if we're on the add-ons step, jump back to the last step for the selected profileCount.
    const answerKey = String(currentStep.dataset?.answerKey || '').trim().toLowerCase();
    if (answerKey === 'addons') {
      this.profileCountSelected = this.getSelectedProfileCount();
      this.applyPersonStepSkipping(this.profileCountSelected);

      const targetIndex = this.findLastPersonStepIndex(this.profileCountSelected);
      if (targetIndex >= 0) {
        this.setActiveStepIndex(targetIndex);
        this.updateNavState();
        return;
      }
    }

    const prevIndex = this.findNextVisibleStepIndex(currentIndex, -1);
    if (prevIndex < 0) return;
    this.setActiveStepIndex(prevIndex);
    this.updateNavState();
  }

  #nextSurveyStep = () => {
    if (!this.canAdvanceFromActiveStep()) {
      const activeStep = this.getActiveStep();
      const emailInput = activeStep?.querySelector('input[type="email"]');
      if (emailInput instanceof HTMLInputElement) emailInput.reportValidity();
      return;
    }

    // If current step is profileCount selector, apply skipping for persons > selected count.
    const activeStep = this.getActiveStep();
    // Only mark "started" after the user confirms their email by clicking Next on the Email step.
    if (activeStep?.dataset?.fieldType === 'Email') {
      void this.maybeTrackSurveyStarted();
    }
    const activeAnswerKey = String(activeStep?.dataset?.answerKey || '').trim().toLowerCase();
    if (activeAnswerKey === 'profilecount') {
      this.profileCountSelected = this.getSelectedProfileCount();
      this.applyPersonStepSkipping(this.profileCountSelected);
    }

    const currentIndex = this.getActiveStepIndex();
    if (currentIndex < 0) return;

    if (activeStep?.dataset?.fieldType === 'Submit') {
      this.formEl?.reset();
      // this.hideSoupTypeOnFormReset();
      this.setActiveStepIndex(0);
      this.updateNavState();
      this.#closeSurveyForm();
      const redirectUrl = this.formEl?.getAttribute('data-redirect-url');
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
      return;
    }

    const nextIndex = this.findNextVisibleStepIndex(currentIndex, 1);
    const nextStep = nextIndex >= 0 ? this.refs.surveyStep?.[nextIndex] : null;
    if (!nextStep) return;

    this.setActiveStepIndex(nextIndex);

    // If the next step is a "Submit" step:
    // - show loading wrapper while submitting
    // - on success: hide wrapper and show the main heading
    // - on failure: hide wrapper and show a generic error in the main heading
    if (nextStep.dataset?.fieldType === 'Submit') {
      const submitStep = this.getActiveStep();
      const mainSubmitHeading = submitStep?.querySelector('.thuysvers-survey-form-heading:not(.thuysvers-survey-form-heading-submit)');

      if (mainSubmitHeading instanceof HTMLElement && !mainSubmitHeading.dataset.originalText) {
        mainSubmitHeading.dataset.originalText = mainSubmitHeading.textContent || '';
      }

      // Hide the main heading while we submit; show the spinner wrapper.
      this.refs.surveySubmitWrapper?.classList.remove('no-disp');
      this.refs.surveyFooterContentWrapper?.classList.add('no-disp');
      mainSubmitHeading?.classList.add('no-disp');

      const payload = this.buildSubmissionJson();

      // Fire-and-handle the async submission (do not block UI thread).
      void (async () => {
        const result = await this.submitToGadgetEndpoint(payload);

        // If submit succeeded, mark `thuysvers_survey_form = completed`
        if (result.ok) {
          const email =
            (payload && payload.submitter && typeof payload.submitter.email === 'string' && payload.submitter.email.trim()) ||
            this.getSubmitterEmailFromForm();
          if (email && EMAIL_REGEX.test(String(email))) {
            try {
              sessionStorage.setItem(STORAGE_KEY_SURVEY_COMPLETED_EMAIL, String(email));
            } catch {
              // ignore
            }
            void this.sendSurveyProgress('completed', String(email), {
              submissionId: payload?.submissionId,
            });
          }
        }

        // If the component was disconnected mid-request, refs may be gone.
        const currentActive = this.getActiveStep();
        if (currentActive?.dataset?.fieldType !== 'Submit') return;

        // Hide loader
        this.refs.surveySubmitWrapper?.classList.add('no-disp');
        this.refs.surveyFooterContentWrapper?.classList.remove('no-disp');

        if (mainSubmitHeading instanceof HTMLElement) {
          if (result.ok) {
            // Restore heading on success
            mainSubmitHeading.textContent = mainSubmitHeading.dataset.originalText || mainSubmitHeading.textContent || '';
          } else {
            // Generic message; vary slightly by status when available
            mainSubmitHeading.textContent = this.getSubmitErrorMessage(result.status);
          }
          mainSubmitHeading.classList.remove('no-disp');
        }
      })();
    }

    this.updateNavState();
  }

  /**
   * Returns a generalized error message for submit failures.
   * @param {number | undefined} status
   * @returns {string}
   */
  getSubmitErrorMessage(status) {
    if (!status) return 'Er ging iets mis bij het versturen van de enquête. Probeer het opnieuw.';
    if (status >= 500) return 'De server is tijdelijk niet beschikbaar. Probeer het later opnieuw.';
    if (status === 429) return 'Je hebt te veel verzoeken gedaan. Probeer het zo nog eens.';
    if (status >= 400) return 'Het versturen is niet gelukt. Controleer je invoer en probeer het opnieuw.';
    return 'Er ging iets mis bij het versturen van de enquête. Probeer het opnieuw.';
  }

  /**
   * Submits the survey JSON to the Gadget endpoint after the user reaches the Submit step.
   * Uses the form `action` URL configured in the section settings.
   *
   * @param {any} payload
   * @returns {Promise<{ ok: true } | { ok: false; status?: number }>}
   */
  async submitToGadgetEndpoint(payload) {
    const form = this.formEl;
    if (!form) return { ok: false };

    if (this.isSubmitting) return { ok: false };
    this.isSubmitting = true;

    try {
      const endpoint = String(form.getAttribute('data-gadget-endpoint') || '').trim();
      if (!endpoint) {
        console.warn('[THUYSVERS-SURVEY] No gadget endpoint configured; skipping submission');
        return { ok: true };
      }

      // we can add meta data here if needed
      const finalPayload = {
        submissionId: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...payload,
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(finalPayload),
      });

      if (!res.ok) {
        console.error('[THUYSVERS-SURVEY] Submission request failed', { status: res.status });
        return { ok: false, status: res.status };
      }

      // Prefer JSON, but don't fail if the endpoint returns plain text.
      const contentType = res.headers.get('content-type') || '';
      const body = contentType.includes('application/json') ? await res.json().catch(() => null) : await res.text().catch(() => '');
      void body;

      // Store the payload in sessionStorage so guest recommendations can be shown without cart attributes.
      this.persistRecommendationsPayload(payload);

      return { ok: true };
    } catch (error) {
      console.error('[THUYSVERS-SURVEY] Failed to submit to gadget endpoint', error);
      return { ok: false };
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Stores the survey payload in sessionStorage under "recommendations" and notifies listeners.
   * @param {any} payload
   */
  persistRecommendationsPayload(payload) {
    try {
      sessionStorage.setItem(STORAGE_KEY_RECOMMENDATIONS, JSON.stringify(payload || {}));
      window.dispatchEvent(new Event(EVENT_RECOMMENDATIONS_UPDATED));
    } catch (error) {
      console.warn('[THUYSVERS-SURVEY] Failed to persist recommendations payload to sessionStorage', error);
    }
  }

  /**
   * Reads the selected profile count from the form (`name="profileCount"`).
   * Supports radio/checkbox/select/number; clamps to [1..4].
   * @returns {number}
   */
  getSelectedProfileCount() {
    const form = this.formEl;
    if (!form) return 1;

    // Support both `profileCount` and `profileCount-p1` naming.
    const directChecked = form.querySelector('input[name="profileCount"]:checked');
    const personChecked = form.querySelector('input[name^="profileCount"]:checked');
    const directField = form.querySelector('[name="profileCount"]');
    const personField = form.querySelector('[name^="profileCount"]');

    /** @type {Element | null} */
    const field = (directChecked || personChecked || directField || personField);
    if (!field) return 1;

    let raw = '';
    if (field instanceof HTMLSelectElement) {
      raw = field.value;
    } else if (field instanceof HTMLInputElement) {
      raw = field.value;
    }

    const n = Number.parseInt(String(raw || '1'), 10);
    if (!Number.isFinite(n)) return 1;
    return Math.min(4, Math.max(1, n));
  }

  /**
   * Marks steps with data-person > profileCount as skipped and disables their form fields
   * so they are not included in FormData or validation.
   * @param {number} profileCount
   */
  applyPersonStepSkipping(profileCount) {
    const steps = this.refs.surveyStep || [];
    for (const step of steps) {
      const person = Number.parseInt(String(step.dataset?.person || ''), 10);
      const isPersonStep = Number.isFinite(person) && person > 0;
      if (!isPersonStep) continue;

      const shouldSkip = person > profileCount;
      step.toggleAttribute('data-skipped', shouldSkip);

      /** @type {(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]} */
      const fields = Array.from(step.querySelectorAll('input, select, textarea')).filter(
        (el) => el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement
      );

      for (const el of fields) {
        // Disable all fields in skipped steps so they don't submit / validate.
        el.disabled = shouldSkip;
      }
    }
  }

  /**
   * Finds the next step index in the given direction that isn't skipped by profileCount rules.
   * @param {number} fromIndex
   * @param {1|-1} direction
   * @returns {number}
   */
  findNextVisibleStepIndex(fromIndex, direction) {
    const steps = this.refs.surveyStep || [];
    let i = fromIndex + direction;
    while (i >= 0 && i < steps.length) {
      const step = steps[i];
      if (!step) {
        i += direction;
        continue;
      }
      const isSkipped = step.hasAttribute('data-skipped');
      if (!isSkipped) return i;
      i += direction;
    }
    return -1;
  }

  /**
   * Finds the last step index with data-person == profileCount (and not skipped).
   * @param {number} profileCount
   * @returns {number}
   */
  findLastPersonStepIndex(profileCount) {
    const steps = this.refs.surveyStep || [];
    let last = -1;
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (!step) continue;
      const person = Number.parseInt(String(step.dataset?.person || ''), 10);
      if (person === profileCount && !step.hasAttribute('data-skipped')) last = i;
    }
    return last;
  }

  /**
   * Builds the JSON payload required by the Submit step from the form state.
   *
   * Assumptions (based on current Liquid):
   * - Per-person fields are named like `${baseKey}-p${n}`
   * - Qty add-ons are number inputs with class `thuysvers-survey-form-qty-input` and name like `soep` / `dessert`
   * - Soup type is a radio group named `soep-type` / `soup-type` with a value containing size + unit
   *
   * @returns {any}
   */
  buildSubmissionJson() {
    const form = this.formEl;
    if (!form) return {};

    const fd = new FormData(form);

    // Submitter email: prefer the email input, fall back to common field name.
    const emailInput = form.querySelector('input[type="email"]');
    const email = String(fd.get('email') || '').trim() || (emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '');

    const profileCount = Number.parseInt(String(fd.get('profileCount') || '1'), 10) || this.getSelectedProfileCount();

    /**
     * @param {number} p
     * @param {string[]} candidates
     * @returns {string}
     */
    const getFirstForPerson = (p, candidates) => {
      const suffix = `-p${p}`;
      for (const base of candidates) {
        const v = fd.get(`${base}${suffix}`);
        const s = String(v ?? '').trim();
        if (s !== '') return s;
      }
      return '';
    };

    /**
     * @param {number} p
     * @param {string[]} candidates
     * @returns {string[]}
     */
    const getAllForPerson = (p, candidates) => {
      const suffix = `-p${p}`;
      for (const base of candidates) {
        const arr = fd.getAll(`${base}${suffix}`).map((x) => String(x));
        if (arr.length) return arr;
      }
      return [];
    };

    /** @type {any[]} */
    const profiles = [];

    for (let p = 1; p <= profileCount; p++) {
      const displayName = getFirstForPerson(p, ['displayName', 'name', 'fullName', 'display_name', 'full_name']);
      const mealsPerWeekRaw = getFirstForPerson(p, ['mealsPerWeek', 'meals_per_week', 'meals']);
      const mealsPerWeek = Number.parseInt(mealsPerWeekRaw || '0', 10) || 0;

      const portionSizeRaw = getFirstForPerson(p, ['portionSize', 'portion_size', 'portion', 'portie']);
      const portionSize = this.parseSizeUnit(portionSizeRaw);

      const diet = getFirstForPerson(p, ['dietType', 'diet_type', 'diet']);
      const dietType = Array.from({ length: Math.max(0, mealsPerWeek) }, () => ({
        diet,
        portionSize,
      }));

      const typeOfKitchen = getAllForPerson(p, ['typeOfKitchen', 'type_of_kitchen', 'kitchen', 'kitchenType']);

      profiles.push({
        profileKey: `p${p}`,
        displayName,
        mealsPerWeek,
        portionSize,
        dietType,
        typeOfKitchen,
      });
    }

    const addOns = this.collectAddOns(form);

    return {
      submitter: { email },
      profileCount,
      profiles,
      addOns,
    };
  }

  /**
   * Collects add-ons from quantity inputs.
   * - creates one entry per add-on input with `quantity: N`
   * - adds soup size/unit option when available
   * @param {HTMLFormElement} form
   * @returns {any[]}
   */
  collectAddOns(form) {
    /** @type {any[]} */
    const addOns = [];

    /** @type {HTMLInputElement[]} */
    const qtyInputs = Array.from(form.querySelectorAll('input.thuysvers-survey-form-qty-input[type="number"]'));

    for (const input of qtyInputs) {
      const rawName = (input.name || '').toLowerCase().trim();
      if (!rawName) continue;

      const qty = this.getQtyInputNumber(input);
      if (qty <= 0) continue;

      const addonType =
        rawName.includes('soep') || rawName.includes('soup')
          ? 'soup'
          : rawName.includes('dessert')
            ? 'dessert'
            : rawName;

      let options;
      if (addonType === 'soup') {
        // Try to find selected soup-type radio near this qty input
        const extrasWrapper = input.closest('.thuysvers-survey-form-extras-wrapper');
        const checked =
          extrasWrapper?.querySelector('.thuysvers-survey-form-soup-type-wrapper input[type="radio"]:checked') ||
          form.querySelector('input[type="radio"][name*="soep-type"]:checked') ||
          form.querySelector('input[type="radio"][name*="soup-type"]:checked');

        if (checked instanceof HTMLInputElement) {
          const parsed = this.parseSizeUnit(checked.value);
          if (parsed.size || parsed.unit) options = parsed;
        }
      }

      const entry = { addonType, quantity: qty };
      if (options) {
        /** @type {any} */ (entry).options = options;
      }
      addOns.push(entry);
    }

    return addOns;
  }

  /**
   * Parses strings like "550g", "550 gram", "0.5 liters" into { size, unit }.
   * @param {string} raw
   * @returns {{ size: string, unit: string }}
   */
  parseSizeUnit(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return { size: '', unit: '' };

    const match = s.match(/(\d+(?:[.,]\d+)?)\s*([a-zA-Z]+)/);
    if (!match) return { size: s, unit: '' };

    const size = (match[1] || '').replace(',', '.');
    let unit = (match[2] || '').toLowerCase();

    // Normalize common units
    if (unit === 'g' || unit === 'gr' || unit === 'gram' || unit === 'grams') unit = 'gram';
    if (unit === 'l' || unit === 'liter' || unit === 'litre' || unit === 'liters' || unit === 'litres') unit = 'liters';

    return { size, unit };
  }


  showSurveyForm = () => {
    document.documentElement.setAttribute('scroll-lock', '');
    this.classList.remove('no-disp');
  }

  #closeSurveyForm = () => {
    document.documentElement.removeAttribute('scroll-lock');
    this.classList.add('no-disp');

    // Reset the "recommended" quick filter state across all instances
    const quickFiltersComponents = document.querySelectorAll('quick-filters-component');
    quickFiltersComponents.forEach((comp) => {
      if (typeof /** @type {any} */ (comp).resetRecommendedFilter === 'function') {
        /** @type {any} */ (comp).resetRecommendedFilter();
      }
    });
  }

}

if (!customElements.get('thuysvers-survey-form-component')) {
  customElements.define('thuysvers-survey-form-component', ThuysversSurveyFormComponent);
}